'use client';

import { Button, VisuallyHidden } from '@zillow/constellation-10';
import { type ComponentProps, useId } from 'react';

export type GateReason = string | false | undefined;

type GatedButtonProps = ComponentProps<typeof Button> & {
  /**
   * Why the button is shut, or `undefined` while it is open. Also pass `undefined`
   * when the reason is one the label already carries — an in-flight submit, say.
   */
  gateReason: GateReason;
};

/**
 * A `Button` that tells assistive tech why it is shut.
 *
 * Constellation's `disabled` prop renders `aria-disabled` and keeps `tabIndex={0}`
 * (see `useDisabledState` in `@zillow/constellation-10`), so the button stays
 * reachable by Tab and a screen reader announces `gateReason`. Without it the button
 * reads only as unavailable, with no way to learn what is missing.
 */
export function GatedButton({
  gateReason,
  children,
  'aria-describedby': callerDescribedBy,
  ...buttonProps
}: GatedButtonProps) {
  const hintId = useId();
  // The gate hint adds to whatever the caller already points at rather than replacing it.
  const describedBy = [callerDescribedBy, gateReason ? hintId : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <>
      <Button {...buttonProps} aria-describedby={describedBy}>
        {children}
      </Button>
      {gateReason ? <VisuallyHidden id={hintId}>{gateReason}</VisuallyHidden> : null}
    </>
  );
}
